import { gql, MutationResult, useMutation } from '@apollo/client';
import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { tacticFragment } from 'basics/graphql/fragments/tactic.fragments';
import { MutationReturnType } from 'basics/types/maya.types';
import { Tactic, TacticInput } from 'generated/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const createTacticGroupMutationCall = gql`
mutation createTacticGroup($createTactic: TacticInput!, $cepId: String!, $relatedTacticsId: [String!]!, $sendToVeeva: Boolean!) {
  createTacticGroup(createTacticInput: $createTactic, cepId: $cepId, relatedTacticsId: $relatedTacticsId, sendToVeeva: $sendToVeeva) {
    ${tacticFragment}
  }
}
`;

type ApiResultType = {
  createTacticGroup: Tactic;
};

type UseCreateTacticGroupMutation = [
  (tactic: Partial<TacticInput>, cepId: string, relatedTacticsId: string[], sendToVeeva?: boolean) => Promise<MutationReturnType<Tactic>>,
  MutationResult<ApiResultType>,
];

export const useCreateTacticGroupMutation = (): UseCreateTacticGroupMutation => {
  const [createTacticGroup, state] = useMutation<ApiResultType>(createTacticGroupMutationCall, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });
  const flags = useFlags();

  const executeMutation = useCallback(async (tactic, cepId: string, relatedTacticsId: string[], sendToVeeva = true) => {
    try {
      const { data } = await createTacticGroup({
        variables: {
          cepId,
          createTactic: tactic,
          relatedTacticsId,
          sendToVeeva: flags.mayaEnableVeevaBiogenlinc1816 && sendToVeeva,
        },
      });

      return { data: data?.createTacticGroup || null, error: null };
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, tactic);
        Logger.logError({
          error,
          customContext,
          transactionName: 'createTacticGroup.ts - useCreateTacticGroupMutation',
        });

        return { data: null, error: customContext.message as string };
      }

      return { data: null, error: null };
    }
  }, [createTacticGroup, flags.mayaEnableVeevaBiogenlinc1816]);

  return [executeMutation, state];
};
